// modules
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// constants
import { Routes, ForgotWatchword as Constants } from "constants/global";
import { Auth } from "app/hooks/Auth";

// components
import Modal from "components/molecules/ModalContainer/ModalContainer";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";
import { validateUsername } from "utils/Validations/fieldValidations";

// hooks
import { useUpdateWatchword, UpdateWatchwordTypes, FormDataValues } from "containers/UpdateWatchword/useUpdateWatchword";
import { AuthenticationDetails } from "amazon-cognito-identity-js";

export type FormValues = {
	username: string;
};

export type UsernameValidType = "valid" | "invalid" | "";
export type UpdateWatchwordPropTypes = UpdateWatchwordTypes & {
	confirmForgotWatchword: (values: FormDataValues) => void;
	isExistingWordUsed: boolean;
	setExistingWordUsed: (arg0: boolean) => void;
};

export type ForgotWatchwordTypes = {
	isValidUsername: UsernameValidType;
	showingModal: boolean;
	username: string | undefined;
	updateWatchwordProps: UpdateWatchwordPropTypes;
	showUpdateWatchword: boolean;
	showForceChangeWatchwordDisclaimer?: boolean;
	handleGoBack: () => void;
	initiateForgotWatchword: (values: FormValues) => void;
	onUsernameChange: (value: string) => void;
};
export type LocationState = {
	state: {
		forceChangeWatchword?: boolean;
	};
};

export function useForgotWatchword(): ForgotWatchwordTypes {
	const updateWatchwordProps = useUpdateWatchword();
	const navigate = useNavigate();
	const location = useLocation();

	const { state } = location as LocationState;

	const [username, setUsername] = useState<string>();
	const [isValidUsername, setValidUsername] = useState<UsernameValidType>("");
	const [showingModal, setShowingModal] = useState<boolean>(false);
	const [showUpdateWatchword, setShowUpdateWatchword] = useState<boolean>(false);
	const [isExistingWordUsed, setExistingWordUsed] = useState<boolean>(false);

	const handleGoBack = useCallback(() => {
		navigate(Routes.LOGIN);
	}, [navigate]);

	const onUsernameChange = useCallback(
		(value: string) => {
			const isValid = validateUsername(value);
			setValidUsername(isValid ? "valid" : "invalid");
			setUsername(value);
		},
		[username, isValidUsername, validateUsername]
	);

	const initiateForgotWatchword = useCallback(
		(values: FormValues) => {
			const { username: _username } = values;
			const isValid = validateUsername(_username);

			if (_username && isValid) {
				const auth = new Auth();
				auth.init(_username);

				const user = auth.getCognitoUser();

				if (user) {
					user.forgotPassword({
						onSuccess: function () {
							setShowingModal(true);
							Modal({
								closable: true,
								title: Constants.FP_REQ_SENT_HEADER,
								subtitle: Constants.FP_REQ_SENT_SUBHEADER,
								okButton: {
									label: Constants.FP_GOTIT_BTN,
									onClick: () => {
										setShowingModal(false);
										setShowUpdateWatchword(true);
									}
								},
								onCloseIconClick: () => {
									setShowUpdateWatchword(true);
									setShowingModal(false);
								}
							});
						},
						onFailure: function (err) {
							const error = err as any;

							setShowingModal(true);
							CountDownModal({
								timer: 3,
								title: error?.message || Constants.FP_SOMETHING_WENT_WRONG,
								errorIcon: true,
								onTimeComplete: () => {
									setShowingModal(false);
									navigate(Routes.LOGIN);
								}
							});
						}
					});
				}
			}
		},
		[username, showingModal, CountDownModal, Modal, navigate, Auth]
	);

	useMemo(() => {
		setExistingWordUsed(false);
	}, [updateWatchwordProps.watchWord]);

	const confirmForgotWatchword = useCallback(
		(values: FormDataValues) => {
			const { watchword, confirm_watchword, verification_code } = values;

			const { isPasswordInvalid } = updateWatchwordProps.validateWatchword(watchword);
			if (isExistingWordUsed) {
				setExistingWordUsed(false);
			}
			if (watchword === confirm_watchword && !isPasswordInvalid && verification_code) {
				const auth = new Auth();
				const user = auth.getCognitoUser();

				if (user) {
					const authenticationDetails = new AuthenticationDetails({
						Username: username || "",
						Password: watchword
					});

					user.authenticateUser(authenticationDetails, {
						onFailure() {
							if (isExistingWordUsed) {
								setExistingWordUsed(false);
							}
							user.confirmPassword(verification_code, watchword, {
								onSuccess: function () {
									setShowingModal(true);

									CountDownModal({
										timer: 3,
										title: Constants.FP_WATCHWORD_SUCCESS,
										onTimeComplete: () => {
											setShowingModal(false);
											setShowUpdateWatchword(false);
											navigate(Routes.LOGIN, { state: { viaForgotWatchword: true, username: username, newWatchword: watchword } });
										}
									});
								},
								onFailure: function (err) {
									const error = err as any;
									if (error?.message?.includes(Constants.FP_INVALID_VERIFICATION_CODE)) {
										updateWatchwordProps.updateVerificationCodeValidation(true);
									} else {
										setShowingModal(true);
										CountDownModal({
											timer: 3,
											errorIcon: true,
											title: error?.message || Constants.FP_SOMETHING_WENT_WRONG,
											onTimeComplete: () => {
												setShowingModal(false);
												setShowUpdateWatchword(false);
												navigate(Routes.LOGIN);
											}
										});
									}
								}
							});
						},
						onSuccess() {
							setExistingWordUsed(true);
							user.signOut();
						},
						totpRequired: function () {
							setExistingWordUsed(true);
							user.signOut();
						},
						newPasswordRequired: function () {
							setExistingWordUsed(true);
							user.signOut();
						}
					});
				}
			}
		},
		[Auth, AuthenticationDetails, isExistingWordUsed, navigate, CountDownModal, showingModal, showUpdateWatchword, updateWatchwordProps]
	);

	return {
		isValidUsername,
		showingModal,
		username,
		updateWatchwordProps: { ...updateWatchwordProps, isExistingWordUsed, setExistingWordUsed, confirmForgotWatchword },
		showUpdateWatchword,
		showForceChangeWatchwordDisclaimer: state?.forceChangeWatchword,
		handleGoBack,
		initiateForgotWatchword,
		onUsernameChange
	};
}
